import { render, h } from "preact"
import { newSearch } from "../actions"
import { SearchQuery } from "../api"
import { FullConfig, ConfigContext, resolveQuery } from "../config"
import { bindClickOutside, findAll, bindBlur } from "../helpers/dom"
import { bindInput } from "../helpers/input"
import { createStore } from "../state"
import { AutocompleteElementProvider } from "./components/AutocompleteElement"
import { HistoryElementProvider } from "./components/HistoryElement"
import { getLocalStorageItem, setLocalStorageItem } from "../helpers/storage"
import ErrorBoundary from "../components/ErrorBoundary"
import createDebouncer from "../utils/debounce"
import { logger, measure, disableNativeAutocomplete } from "@nosto/search-js/utils"
import { selectorLoaded } from "../helpers/selectors"
import { extractNativeForm, searchFormNativeClassName } from "../helpers/searchFallback"
import { isErrorSession } from "../helpers/errorHandling"
import { resolveCssSelector } from "../helpers/resolveCssSelector"
import { AutocompleteOptions } from "./components/types"
import { AutocompletePageProvider } from "@nosto/search-js/preact/autocomplete"

export type AutocompleteHandlers = {
    onSubmit: (query: SearchQuery, options?: AutocompleteOptions) => void
}

export async function initAutocomplete(config: FullConfig, handlers: AutocompleteHandlers) {
    if (!config.inputCssSelector || isErrorSession()) {
        return
    }
    const end = measure("initAutocomplete")

    if (config.formCssSelector) {
        await extractNativeForm(config.formCssSelector, config.formUnbindDelay)
    }

    const store = createStore({
        pageType: "autocomplete",
        query: resolveQuery(config, "autocompleteQuery")
    })

    const debouncer = createDebouncer(config.debounceDelay)

    const dropdowns = findAll(config.inputCssSelector, HTMLInputElement)
        .filter(input => !input.form?.classList.contains(searchFormNativeClassName))
        .map(input => {
            const dropdown = createDropdown(input, config)
            const history = createHistory(input, config)

            if (config.speechToTextComponent && config.speechToTextEnabled) {
                const locationHelperElement = document.createElement("div")
                locationHelperElement.className = "ns-autocomplete-voice-position"
                input.insertAdjacentElement("afterend", locationHelperElement)

                render(
                    <ErrorBoundary>
                        <config.speechToTextComponent onSubmit={query => handlers.onSubmit({ query })} />
                    </ErrorBoundary>,
                    locationHelperElement
                )
            }

            const storeHistoryState = () => {
                store.updateState({
                    historyItems: history.get()
                })
            }

            input.setAttribute("data-nosto-element", "search-input")
            storeHistoryState()

            const createHandleSubmit =
                (element: ReturnType<typeof createDropdown | typeof createHistory>) =>
                (query: SearchQuery, options?: AutocompleteOptions) => {
                    handlers.onSubmit(query, options)
                    element.hide()
                    if (query.query) {
                        history.add(query.query)
                        storeHistoryState()
                        input.value = query.query
                    }
                }

            const shouldUpdateSearch = async (value: string) => {
                dropdown.hide()
                const query = store.getState()?.query?.query
                if (query !== value) {
                    await newSearch({ config, store }, { query: value })
                }
                dropdown.show()
            }

            disableNativeAutocomplete(input)

            let historyInitialized = false
            function initializeHistory() {
                if (!config.historyComponent) {
                    return
                }
                history.show()
                if (historyInitialized) {
                    return
                }

                render(
                    <ErrorBoundary>
                        <AutocompletePageProvider config={config} store={store}>
                            <ConfigContext.Provider value={config}>
                                <HistoryElementProvider onHistorySubmit={createHandleSubmit(history)}>
                                    <config.historyComponent />
                                </HistoryElementProvider>
                            </ConfigContext.Provider>
                        </AutocompletePageProvider>
                    </ErrorBoundary>,
                    history.element
                )
                historyInitialized = true
            }

            const bind = bindInput(config, input, {
                onInput: value => {
                    if (value.length >= config.autocompleteMinLength) {
                        dropdown.show()
                        history.hide()
                        debouncer(async () => {
                            try {
                                return await newSearch(
                                    { config, store },
                                    {
                                        query: value
                                    }
                                )
                            } catch (err) {
                                logger.error("New search failed", err)
                                return
                            }
                        })
                    } else if (value.length === 0 && config.historyEnabled) {
                        dropdown.hide()
                        initializeHistory()
                    } else {
                        dropdown.hide()
                        history.hide()
                    }
                },
                onFocus: async value => {
                    if (value.length >= config.autocompleteMinLength) {
                        if (!value) {
                            store.updateState({
                                initialized: true,
                                loading: false
                            })
                        }
                    } else if (value.length === 0 && config.historyEnabled) {
                        initializeHistory()
                    }
                },
                onClick: async value => {
                    if (value.length >= config.autocompleteMinLength) {
                        shouldUpdateSearch(value)

                        if (!value) {
                            store.updateState({
                                initialized: true,
                                loading: false
                            })
                        }
                    } else if (value.length === 0 && config.historyEnabled && !history.isOpen()) {
                        if (!config.historyComponent || !config.historyEnabled) {
                            return
                        }
                        history.show()
                    }
                },
                onSubmit: value => {
                    if (value.length >= config.autocompleteMinLength) {
                        dropdown.hide()
                        history.hide()
                        history.add(value)
                        storeHistoryState()

                        const highlighted = dropdown.highlightedIndex()
                        if (highlighted >= 0) {
                            dropdown.submitHighlightedItem(highlighted)
                            return
                        }

                        handlers.onSubmit({
                            query: value
                        })

                        dropdown.hide()
                    }
                },
                onKeyDown: (_value, key) => {
                    if (_value.length >= config.autocompleteMinLength) {
                        if (dropdown.isOpen()) {
                            if (key === "ArrowDown") {
                                dropdown.goDown()
                            } else if (key === "ArrowUp") {
                                dropdown.goUp()
                            } else if (key === "Enter") {
                                const highlighted = dropdown.highlightedIndex()
                                if (highlighted >= 0) {
                                    dropdown.submitHighlightedItem(highlighted)
                                    return
                                }
                                dropdown.hide()
                            }
                        }

                        if (key === "Escape") {
                            dropdown.hide()
                        } else if (key === "ArrowDown") {
                            dropdown.show()
                            history.hide()
                        }
                    }

                    if (_value.length === 0 && config.historyEnabled) {
                        if (history.isOpen()) {
                            if (key === "ArrowDown") {
                                history.goDown()
                            } else if (key === "ArrowUp") {
                                history.goUp()
                            }
                        }
                        if (key === "Escape") {
                            history.hide()
                        } else if (key === "ArrowDown") {
                            history.show()
                        } else if (key === "Enter") {
                            const highlighted = history.highlightedIndex()
                            if (highlighted >= 0) {
                                history.submitHighlightedItem(highlighted)
                                return
                            }
                            history.hide()
                        }
                    }
                }
            })

            store.onChange(selectorLoaded, ({ initialized }) => {
                if (!initialized || !config.autocompleteComponent) {
                    return
                }

                const end = measure("renderAutocomplete")

                render(
                    <ErrorBoundary>
                        <AutocompletePageProvider config={config} store={store}>
                            <ConfigContext.Provider value={config}>
                                <AutocompleteElementProvider onSubmit={createHandleSubmit(dropdown)}>
                                    <config.autocompleteComponent />
                                </AutocompleteElementProvider>
                            </ConfigContext.Provider>
                        </AutocompletePageProvider>
                    </ErrorBoundary>,
                    dropdown.element
                )
                end()
            })

            const outsideDropdown = bindClickOutside([dropdown.element, input], () => {
                dropdown.hide()
            })

            const outsideHistory = bindClickOutside([history.element, input], () => {
                history.hide()
            })

            bindBlur(history.element, history.hide)

            bindBlur(dropdown.element, dropdown.hide)

            return {
                destroy() {
                    bind.destroy()
                    dropdown.destroy()
                    history.destroy()
                    outsideDropdown.destroy()
                    outsideHistory.destroy()
                }
            }
        })

    end()

    return {
        destroy: () => {
            dropdowns.forEach(v => {
                v.destroy()
            })
        }
    }
}

function createElements(input: HTMLInputElement, dropdown: HTMLDivElement, config: FullConfig) {
    const wrapper = document.createElement("div")
    wrapper.className = "nosto-autocomplete-wrapper"

    dropdown.style.display = "none"

    input.parentNode?.insertBefore(wrapper, input.nextSibling)
    wrapper.appendChild(input)

    const dropdownLocator = resolveCssSelector(config.dropdownCssSelector)
    const dropdownParent = dropdownLocator && document.querySelector(dropdownLocator.selector)

    if (dropdownParent) {
        const form = document.createElement("form")
        form.className = "nosto-dropdown-form"
        form.appendChild(dropdown)
        if (dropdownLocator.position === "first") {
            dropdownParent.prepend(form)
        } else {
            dropdownParent.appendChild(form)
        }
    } else {
        wrapper.appendChild(dropdown)
    }

    const highlightElement = (elementList: Element[], highlightIndex: number) => {
        dropdown
            .querySelector(".ns-autocomplete-element.ns-autocomplete-element-hovered")
            ?.classList.remove("ns-autocomplete-element-hovered")
        elementList[highlightIndex]?.classList.add("ns-autocomplete-element-hovered")
    }

    const getHighlightIndex = (removeHighlight?: boolean): number => {
        const elements = Array.from(dropdown.getElementsByClassName("ns-autocomplete-element"))

        const highlighted = elements.find(item => {
            if (item.classList.contains("ns-autocomplete-element-hovered")) {
                if (removeHighlight) {
                    item.classList.remove("ns-autocomplete-element-hovered")
                }
                return true
            }
            return false
        })

        return highlighted ? elements.indexOf(highlighted) : -1
    }
    return {
        element: dropdown,
        hide: () => {
            dropdown.style.display = "none"
        },
        show: () => {
            dropdown.style.display = ""
        },
        destroy: () => {
            wrapper.parentElement?.removeChild(wrapper)
        },
        isOpen: () => dropdown.style.display !== "none",
        goDown: () => {
            const elements = Array.from(dropdown.getElementsByClassName("ns-autocomplete-element"))
            const index = getHighlightIndex(true)
            highlightElement(elements, index >= 0 ? index + 1 : 0)
        },
        goUp: () => {
            const elements = Array.from(dropdown.getElementsByClassName("ns-autocomplete-element"))
            const index = getHighlightIndex(true)
            highlightElement(elements, (index >= 0 ? index : elements.length) - 1)
        },
        highlight: highlightElement,
        highlightedIndex: getHighlightIndex,
        submitHighlightedItem: (highlightedIndex: number) => {
            const elements = Array.from(dropdown.querySelectorAll<HTMLElement>(".ns-autocomplete-element"))
            elements[highlightedIndex]?.click()
            const value = elements[highlightedIndex]?.getAttribute("data-nosto-query")
            if (value) {
                input.value = value
            }
        }
    }
}

function createDropdown(input: HTMLInputElement, config: FullConfig) {
    const dropdown = document.createElement("div")
    const base = createElements(input, dropdown, config)

    return {
        ...base,
        element: dropdown
    }
}

const historyKey = "nosto:search-templates:history"

function createHistory(input: HTMLInputElement, config: FullConfig) {
    const dropdown = document.createElement("div")
    const base = createElements(input, dropdown, config)

    return {
        ...base,
        element: dropdown,
        add: (value: string) => {
            const allItems = getLocalStorageItem<string[]>(historyKey) || []
            const filteredItems = allItems.filter(v => v !== value).slice(config.historySize ? -config.historySize : 0)
            filteredItems.push(value)
            setLocalStorageItem(historyKey, filteredItems)
        },
        get: () => {
            const historyFromLocalStorage = getLocalStorageItem<string[]>(historyKey) || []
            const historyItems = historyFromLocalStorage
                ? historyFromLocalStorage.reverse().filter((c: string) => !!c)
                : undefined

            return historyItems
        }
    }
}
